import { CoreHTTPBaseRepository } from "src/contexts/_shared/infrastructure/core/CoreHTTPBaseRepository";
import { AcceptanceCheckbox } from "../../domain/AcceptanceCheckbox";
import { Actor } from "../../domain/Actor";
import { DocumentationArea } from "../../domain/DocumentationArea";
import { DocumentationAreaId } from "../../domain/DocumentationAreaId";
import { DocumentationAreaNotificationTypes } from "../../domain/DocumentationAreaNotification";
import {
  DocumentationAreaRepository,
  ExchangeSlugByDocumentationAreaIdAndActorIdResponse,
} from "../../domain/DocumentationAreaRepository";
import { IdNumberTypes } from "../../domain/IdNumberTypes";
import {
  DocumentationAreaActorBasicDataResponse,
  DocumentationAreaResponse,
  IdNumber,
  IdNumberValue,
} from "./DocumentationAreaResponse";
import { ActorId } from "../../domain/ActorId";
import { StorageFileWithContent } from "src/contexts/_shared/domain/StorageFileWithContent";
import { ActorBasicData } from "src/contexts/_shared/domain/ActorBasicData";
import { UserLink } from "../../domain/UserLink";
import { Customization } from "../../domain/Customization";
import { LinkTypes } from "src/contexts/_shared/domain/LinkType";
import { IdentityVerificationDocumentTypes } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentTypes";
import { OrderId } from "../../domain/OrderId";
import { OrderPayslip } from "../../domain/OrderPayslip";
import { OrderIdentityVerification } from "../../domain/OrderIdentityVerification";
import { OrderModel } from "../../domain/OrderModel";
import { OrderOtherDocument } from "../../domain/OrderOtherDocument";
import { DocumentationAreaTypes } from "src/contexts/documentationArea/domain/DocumentationAreaTypes";
import { ActorIdentity } from "src/contexts/documentationArea/domain/ActorIdentity";
import { ActorIdentityRepresentative } from "src/contexts/documentationArea/domain/ActorIdentityRepresentative";
import { ActorIdentityRepresentedEntity } from "src/contexts/documentationArea/domain/ActorIdentityRepresentedEntity";
import { PrefilledIdNumberInput } from "src/contexts/documentationArea/domain/ActorPrefilledData";
import { OpenAdminBanner } from "../../domain/OpenAdminBanner";
import { OpenAdminBannerStatus } from "../../domain/OpenAdminBannerStatus";
import { DocumentationAreaActorBasicData } from "src/contexts/documentationArea/domain/DocumentationAreaActorBasicData";

export class HTTPDocumentationAreaRepository
  extends CoreHTTPBaseRepository
  implements DocumentationAreaRepository
{
  async getDocumentationArea(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string | null,
  ) {
    const documentationAreaResponse = await this.request<DocumentationAreaResponse>({
      method: "GET",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "accept-language": locale ?? "",
      },
    });

    const mapIdNumbers = (idNumbers?: IdNumberValue[]) => {
      return (
        idNumbers?.map((idNumber) => ({
          number: idNumber.number,
          type: idNumber.type as IdNumberTypes,
          country: idNumber.country,
          verified: idNumber.verified,
        })) || []
      );
    };

    const mapPrefilledIdNumbers = (idNumbers?: IdNumber[]): PrefilledIdNumberInput[] => {
      return (
        idNumbers?.map((idNumber) => ({
          type: {
            value: idNumber.type.value,
          },
          data: {
            number: {
              value: idNumber.data.number.value,
              editable: idNumber.data.number.editable,
            },
            country: {
              value: idNumber.data.country.value,
              editable: idNumber.data.country.editable,
            },
          },
        })) || []
      );
    };

    const actorData = documentationAreaResponse.actor;
    const representative = actorData.identity.representative;
    const representedEntity = actorData.identity.representedEntity;
    const prefilledRepresentative = actorData.prefilledData?.identity?.representative;
    const prefilledRepresentedEntity = actorData.prefilledData?.identity?.representedEntity;

    const actor = new Actor({
      id: actorData.id,
      identity: new ActorIdentity(
        new ActorIdentityRepresentative({
          name: representative?.name ?? null,
          firstSurname: representative?.firstSurname ?? null,
          secondSurname: representative?.secondSurname ?? null,
          email: representative?.email ?? null,
          idNumbers: mapIdNumbers(representative?.idNumbers),
        }),
        new ActorIdentityRepresentedEntity({
          legalName: representedEntity?.legalName ?? null,
          individualRelationType: representedEntity?.individualRelationType ?? null,
          idNumbers: mapIdNumbers(representedEntity?.idNumbers),
        }),
      ),
      signedAcceptanceIds: actorData.signedAcceptanceIds,
      notifications: actorData.notifications.map((notification) => ({
        id: notification.id,
        type: notification.type as DocumentationAreaNotificationTypes,
        message: notification.message,
        messageParams: notification.messageParams ?? {},
        externalId: notification.externalId,
        delivered: notification.delivered,
        createdAt: notification.createdAt,
      })),
      orders: actorData.orders
        .map((order) => {
          if (order.type === "payslip") {
            return OrderPayslip.fromPrimitives(order);
          } else if (order.type === "model") {
            return OrderModel.fromPrimitives(order);
          } else if (order.type === "identity_verification") {
            return OrderIdentityVerification.fromPrimitives(order);
          } else if (order.type === "other_document") {
            return OrderOtherDocument.fromPrimitives(order);
          } else {
            throw new Error(`Unknown order type: ${(order as any).type}`);
          }
        })
        .filter(Boolean),
      openAdminBanner: actorData.openAdminBanner
        ? new OpenAdminBanner({
            status: actorData.openAdminBanner.status as OpenAdminBannerStatus,
            title: actorData.openAdminBanner.title,
            subtitle: actorData.openAdminBanner.subtitle,
            buttonText: actorData.openAdminBanner.buttonText,
            sessionLink: actorData.openAdminBanner.sessionLink,
          })
        : undefined,
      prefilledData: {
        identity: {
          representative: {
            email: prefilledRepresentative?.email?.value
              ? {
                  value: prefilledRepresentative.email.value ?? null,
                  editable: prefilledRepresentative.email.editable ?? true,
                }
              : null,
            name: prefilledRepresentative?.name?.value
              ? {
                  value: prefilledRepresentative.name.value ?? null,
                  editable: prefilledRepresentative.name.editable ?? true,
                }
              : null,
            firstSurname: prefilledRepresentative?.firstSurname?.value
              ? {
                  value: prefilledRepresentative.firstSurname.value ?? null,
                  editable: prefilledRepresentative.firstSurname.editable ?? true,
                }
              : null,
            secondSurname: prefilledRepresentative?.secondSurname?.value
              ? {
                  value: prefilledRepresentative.secondSurname.value ?? null,
                  editable: prefilledRepresentative.secondSurname.editable ?? true,
                }
              : null,
            idNumbers: mapPrefilledIdNumbers(prefilledRepresentative?.idNumbers),
          },
          representedEntity: {
            legalName: prefilledRepresentedEntity?.legalName?.value
              ? {
                  value: prefilledRepresentedEntity.legalName.value ?? null,
                  editable: prefilledRepresentedEntity.legalName.editable ?? true,
                }
              : null,
            individualRelationType: prefilledRepresentedEntity?.individualRelationType?.value
              ? {
                  value: prefilledRepresentedEntity.individualRelationType.value ?? null,
                  editable: prefilledRepresentedEntity.individualRelationType.editable ?? true,
                }
              : null,
            idNumbers: mapPrefilledIdNumbers(prefilledRepresentedEntity?.idNumbers),
          },
        },
      },
    });

    return new DocumentationArea({
      id: documentationAreaResponse.id,
      type: documentationAreaResponse.type as DocumentationAreaTypes,
      actor,
      availableActors: documentationAreaResponse.availableActors.map((availableActor) => ({
        id: availableActor.id,
        readOnly: availableActor.readOnly,
        fullName: availableActor.fullName,
      })),
      templateName: actorData.templateName,
      availableTemplates: actorData.availableTemplates,
      locale: documentationAreaResponse.locale,
      actorInvitation: documentationAreaResponse.actorInvitation
        ? {
            enabled: documentationAreaResponse.actorInvitation.enabled,
            reason: documentationAreaResponse.actorInvitation.reason,
          }
        : null,
    });
  }

  async getCustomization(slug: string, linkType: LinkTypes) {
    const response = await this.request<Customization>({
      method: "GET",
      url: `/internal/documentation-area/customization`,
      params: { slug, linkType },
    });
    return response;
  }

  async getCustomizationByDocumentationAreaId(
    documentationAreaId: DocumentationAreaId,
    accessToken: string,
  ): Promise<Customization> {
    const response = await this.request<Customization>({
      method: "GET",
      url: `/internal/documentation-area/${documentationAreaId}/customization`,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  }

  async deliverNotification(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    notificationId: string,
  ) {
    await this.request<{}>({
      method: "PATCH",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/notification/${notificationId}/delivered`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  async getConsents(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
  ): Promise<AcceptanceCheckbox[]> {
    const response = await this.request<{ acceptanceCheckboxes: AcceptanceCheckbox[] }>({
      method: "GET",
      url: `/internal/documentation-area/${documentationAreaId}/consents`,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.acceptanceCheckboxes;
  }

  async getHasSignedAcceptance(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
  ): Promise<boolean> {
    const response = await this.request<{ hasSignedAcceptance: boolean }>({
      method: "GET",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/acceptance`,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.hasSignedAcceptance;
  }

  async getDocumentationAreaIdAndActorIdByMagicLinkSlug(
    accessToken: string,
    slug: string,
  ): Promise<ExchangeSlugByDocumentationAreaIdAndActorIdResponse> {
    const response = await this.request<ExchangeSlugByDocumentationAreaIdAndActorIdResponse>({
      method: "GET",
      url: `/internal/documentation-area/magic-link/${slug}`,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return {
      documentationAreaId: response.documentationAreaId,
      actorId: response.actorId,
    };
  }

  async getDocumentationAreaIdAndActorIdByUserLinkSlug(
    accessToken: string,
    slug: string,
  ): Promise<ExchangeSlugByDocumentationAreaIdAndActorIdResponse> {
    const response = await this.request<ExchangeSlugByDocumentationAreaIdAndActorIdResponse>({
      method: "GET",
      url: `/internal/documentation-area/user-link/${slug}`,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return { documentationAreaId: response.documentationAreaId, actorId: response.actorId };
  }

  public async patchLocale(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string,
  ) {
    await this.axiosInstance.request<{}>({
      method: "PATCH",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/set-locale`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: { locale },
    });
  }

  public async updateBasicData(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    basicData: ActorBasicData,
  ) {
    await this.request<{}>({
      method: "PATCH",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/basic-data`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: basicData,
    });
  }

  public async getActorBasicData(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
  ): Promise<DocumentationAreaActorBasicData> {
    const response = await this.request<DocumentationAreaActorBasicDataResponse>({
      method: "GET",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/basic-data`,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return {
      isFullfilled: response.isFullfilled,
      prefilledData: response.prefilledData,
      availableTemplates: response.availableTemplates,
    };
  }

  public async updateActorFromActorTemplateId(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    templateId: string,
  ): Promise<void> {
    await this.request<{}>({
      method: "PATCH",
      url: `/documentation-area/${documentationAreaId}/actor/${actorId}/actor-template/${templateId}`,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  public async createActorInvitation(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    input: { email: string; actorId: ActorId },
  ): Promise<void> {
    await this.request<{}>({
      method: "POST",
      url: `/documentation-area/${documentationAreaId}/actor-invitation`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: input,
    });
  }

  public async patchActorOrder(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    orderId: OrderId,
    files: StorageFileWithContent[],
  ) {
    await this.request<{}>({
      method: "PATCH",
      url: `/documentation-area/${documentationAreaId}/actor/${actorId}/order/${orderId}`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: {
        files: files.map((file) => file.toPrimitives()),
      },
    });
  }

  public async patchActorIDVOrder(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    orderId: OrderId,
    documentType?: IdentityVerificationDocumentTypes,
    issuingCountry?: string,
    frontFile?: StorageFileWithContent,
    backFile?: StorageFileWithContent,
  ) {
    await this.request<{}>({
      method: "PATCH",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/order/${orderId}/identity-verification`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: {
        documentType,
        issuingCountry,
        frontFile: frontFile?.toPrimitives(),
        backFile: backFile?.toPrimitives(),
      },
    });
  }

  public async createSignedAcceptances(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    signedAcceptanceCheckboxes: AcceptanceCheckbox[],
  ) {
    await this.request<{}>({
      method: "POST",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/acceptance`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: {
        acceptances: signedAcceptanceCheckboxes,
      },
    });
  }

  public async getUserLink(slug: string) {
    const userLink = await this.request<UserLink>({
      method: "GET",
      url: `/internal/documentation-area/user-link/slug/${slug}`,
    });
    return userLink;
  }

  public async createOpenAdminSession(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string,
  ) {
    return await this.request<{ id: string; widgetLink: string }>({
      method: "POST",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/open-admin/session`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: { locale },
    });
  }

  public async createOpenAdminIDVSession(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string,
  ) {
    return await this.request<{ id: string; widgetLink: string }>({
      method: "POST",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/open-admin/idv-session`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: { locale },
    });
  }

  public async postDiscardIdentityVerificationStagedFile(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    identityVerificationId: string,
    fileId: string,
  ) {
    await this.request({
      method: "POST",
      url: `/internal/documentation-area/${documentationAreaId}/actor/${actorId}/identity-verification/${identityVerificationId}/file/${fileId}/discard`,
      headers: { Authorization: `Bearer ${accessToken}` },
      data: {
        fileId,
      },
    });
  }

  async track(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    type: string,
    properties: { [key: string]: string | number | boolean } = {},
    userProps?: { [key: string]: string | number | boolean },
  ) {
    await this.request<{}>({
      method: "POST",
      url: `/internal/documentation-area/${documentationAreaId}/event`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      data: {
        type,
        properties,
        user_props: userProps,
        actorId,
      },
    });
  }

  async trackUserLink(
    userLinkId: string,
    type: string,
    properties: { [key: string]: string | number | boolean } = {},
    userProps?: { [key: string]: string | number | boolean },
  ) {
    await this.request<{}>({
      method: "POST",
      url: `/internal/documentation-area/event`,
      data: {
        userLinkId,
        type,
        properties,
        user_props: userProps,
      },
    });
  }
}
